/**
 * RevisionEditor Page
 *
 * The editing interface for revising stories.
 * Works with both sample revisions and challenge submission revisions.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChevronLeft,
  RefreshCw,
  Save,
  CheckCircle,
  Loader2,
  RotateCcw,
  Send,
  Eye,
  EyeOff,
} from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { StructureChecklist } from '@/components/challenge'
import { BeforeAfterView, ContentComparison } from '@/components/revise'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getRevisionSample,
  getInProgressRevisionForSample,
  getInProgressRevisionForSubmission,
  createRevisionFromSample,
  createRevisionFromSubmission,
  updateRevisionContent,
  completeRevision,
  revertToOriginal,
} from '@/services/revisionService'
import { getSubmission } from '@/services/challengeService'
import { analyzeStructure } from '@/services/structureAnalyzer'
import { cn } from '@/lib/utils'
import type { RevisionSample, StoryRevision, ChallengeSubmission } from '@/types/database'
import type { StructureAnalysisResult } from '@/types/domain'

// Autosave debounce time in ms
const AUTOSAVE_DELAY = 1500

export function RevisionEditor() {
  const { sourceType, sourceId } = useParams<{ sourceType: string; sourceId: string }>()
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  // Source data
  const [sample, setSample] = useState<RevisionSample | null>(null)
  const [_submission, setSubmission] = useState<ChallengeSubmission | null>(null)

  // Revision data
  const [revision, setRevision] = useState<StoryRevision | null>(null)
  const [content, setContent] = useState('')

  // Analysis state
  const [currentAnalysis, setCurrentAnalysis] = useState<StructureAnalysisResult | null>(null)

  // UI state
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [showOriginal, setShowOriginal] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Load data
  useEffect(() => {
    async function loadData() {
      if (!sourceType || !sourceId || !activeWriter) return

      setIsLoading(true)
      try {
        if (sourceType === 'sample') {
          // Load sample and check for existing revision
          const sampleData = await getRevisionSample(sourceId)
          if (!sampleData) {
            showToast('Sample not found', 'error')
            navigate('/revise')
            return
          }
          setSample(sampleData)

          // Check for existing in-progress revision
          let revisionData = await getInProgressRevisionForSample(activeWriter.id, sourceId)

          if (!revisionData) {
            // Create new revision
            revisionData = await createRevisionFromSample(activeWriter.id, sourceId)
          }

          setRevision(revisionData)
          setContent(revisionData.revised_content)
          setIsCompleted(revisionData.status === 'completed')

          // Analyze current content
          const analysis = analyzeStructure(revisionData.revised_content)
          setCurrentAnalysis(analysis)

        } else if (sourceType === 'submission') {
          // Load submission and check for existing revision
          const submissionData = await getSubmission(sourceId)
          if (!submissionData) {
            showToast('Submission not found', 'error')
            navigate('/revise')
            return
          }
          setSubmission(submissionData)

          // Check for existing in-progress revision
          let revisionData = await getInProgressRevisionForSubmission(activeWriter.id, sourceId)

          if (!revisionData) {
            // Create new revision
            revisionData = await createRevisionFromSubmission(activeWriter.id, sourceId)
          }

          setRevision(revisionData)
          setContent(revisionData.revised_content)
          setIsCompleted(revisionData.status === 'completed')

          // Analyze current content
          const analysis = analyzeStructure(revisionData.revised_content)
          setCurrentAnalysis(analysis)

        } else {
          showToast('Invalid source type', 'error')
          navigate('/revise')
        }
      } catch (error) {
        console.error('Error loading revision:', error)
        showToast('Failed to load revision', 'error')
        navigate('/revise')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [sourceType, sourceId, activeWriter, navigate, showToast])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.max(300, textareaRef.current.scrollHeight)}px`
    }
  }, [content])

  // Autosave with debounce
  const handleChange = useCallback(
    (newContent: string) => {
      setContent(newContent)

      // Update live analysis
      const analysis = analyzeStructure(newContent)
      setCurrentAnalysis(analysis)

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      // Set new timeout for autosave
      if (revision) {
        saveTimeoutRef.current = setTimeout(async () => {
          setIsSaving(true)
          try {
            await updateRevisionContent(revision.id, newContent)
            setLastSaved(new Date())
          } catch (error) {
            console.error('Autosave failed:', error)
          } finally {
            setIsSaving(false)
          }
        }, AUTOSAVE_DELAY)
      }
    },
    [revision]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // Manual save
  const handleManualSave = async () => {
    if (!revision) return

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    setIsSaving(true)
    try {
      await updateRevisionContent(revision.id, content)
      setLastSaved(new Date())
    } catch (error) {
      console.error('Save failed:', error)
      showToast('Failed to save', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  // Revert to original
  const handleRevert = async () => {
    if (!revision) return

    try {
      const updated = await revertToOriginal(revision.id)
      setRevision(updated)
      setContent(updated.revised_content)
      setCurrentAnalysis(analyzeStructure(updated.revised_content))
      showToast('Reverted to original', 'success')
    } catch (error) {
      console.error('Revert failed:', error)
      showToast('Failed to revert', 'error')
    }
  }

  // Complete revision
  const handleComplete = async () => {
    if (!revision) return

    // Save any pending changes first
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    setIsSubmitting(true)
    try {
      // Save current content first
      await updateRevisionContent(revision.id, content)

      // Complete the revision
      const result = await completeRevision(revision.id)
      setRevision(result.revision)
      setIsCompleted(true)
      showToast('Revision complete! See your improvement.', 'success')
    } catch (error) {
      console.error('Complete failed:', error)
      showToast('Failed to complete revision', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading || !revision) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-accent)]" />
      </div>
    )
  }

  const title = sample?.title || 'Story Revision'
  const hints = sample?.hints as { tip?: string } | null
  const originalAnalysis = revision.original_analysis as StructureAnalysisResult | null

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/revise">
              <Button variant="ghost" size="sm">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate">
                {title}
              </h1>
              <div className="flex items-center gap-2 text-sm">
                <RefreshCw className="w-4 h-4 text-[var(--color-kids-accent)]" />
                <span className="text-[var(--color-kids-accent)]">
                  Revision Studio
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {isCompleted ? (
          // Completed view - show before/after comparison
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card className="bg-[var(--color-kids-success)]/5 border-[var(--color-kids-success)]/20">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-[var(--color-kids-success)]" />
                <h2 className="font-display font-bold text-xl text-[var(--color-kids-text)]">
                  Revision Complete!
                </h2>
                <p className="text-[var(--color-kids-text-muted)] mt-2">
                  Great work! Here's how your story improved.
                </p>
              </CardContent>
            </Card>

            {/* Before/After Analysis */}
            {originalAnalysis && currentAnalysis && (
              <BeforeAfterView
                originalAnalysis={originalAnalysis}
                revisedAnalysis={currentAnalysis}
              />
            )}

            {/* Content Comparison */}
            <ContentComparison
              originalContent={revision.original_content}
              revisedContent={revision.revised_content}
            />

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <Link to="/revise">
                <Button variant="secondary">
                  Back to Revision Studio
                </Button>
              </Link>
              <Link to="/challenge">
                <Button variant="primary">
                  Try a Challenge
                </Button>
              </Link>
            </div>
          </motion.div>
        ) : (
          // Editing view
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Instructions Card */}
            {sample && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-[var(--color-kids-text)]">
                    {sample.description}
                  </p>
                  {hints?.tip && (
                    <div className="mt-3 pt-3 border-t border-[var(--color-kids-surface-alt)]">
                      <p className="text-sm text-[var(--color-kids-text-muted)]">
                        <span className="font-semibold">Tip:</span> {hints.tip}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Toggle Original View */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOriginal(!showOriginal)}
              >
                {showOriginal ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-1" />
                    Hide Original
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-1" />
                    Show Original
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleRevert}
                disabled={content === revision.original_content}
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Revert to Original
              </Button>
            </div>

            {/* Original Content (collapsible) */}
            {showOriginal && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Card className="bg-[var(--color-kids-surface-alt)]">
                  <CardContent className="p-4">
                    <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text-muted)] mb-2">
                      Original Story
                    </h4>
                    <p className="text-[var(--color-kids-text)] whitespace-pre-wrap">
                      {revision.original_content}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Editor */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={e => handleChange(e.target.value)}
                placeholder="Edit the story here..."
                disabled={isSubmitting}
                className={cn(
                  'w-full min-h-[300px] p-4 rounded-[var(--radius-kid-lg)]',
                  'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-accent)]/20',
                  'text-[var(--color-kids-text)] text-[var(--font-size-kid-base)]',
                  'font-body leading-relaxed',
                  'placeholder:text-[var(--color-kids-text-light)]',
                  'focus:outline-none focus:border-[var(--color-kids-accent)] focus:ring-2 focus:ring-[var(--color-kids-accent)]/20',
                  'resize-none transition-all',
                  isSubmitting && 'opacity-50 cursor-not-allowed'
                )}
              />

              {/* Saving indicator */}
              <div className="absolute bottom-3 right-3 flex items-center gap-2">
                {isSaving && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-1 text-xs text-[var(--color-kids-text-muted)]"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Saving...
                  </motion.span>
                )}
                {!isSaving && lastSaved && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-1 text-xs text-[var(--color-kids-success)]"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Saved
                  </motion.span>
                )}
              </div>
            </div>

            {/* Live Analysis */}
            {currentAnalysis && (
              <Card>
                <CardContent className="p-4">
                  <h4 className="font-display font-semibold text-sm text-[var(--color-kids-text)] mb-3">
                    Current Structure
                  </h4>
                  <StructureChecklist
                    analysis={currentAnalysis}
                    mode="coach"
                    expandable={false}
                  />
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleManualSave}
                disabled={isSaving || isSubmitting}
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleComplete}
                disabled={isSaving || isSubmitting || content === revision.original_content}
                isLoading={isSubmitting}
              >
                <Send className="w-4 h-4 mr-1" />
                Complete Revision
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  )
}

export default RevisionEditor
